import { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
  Grid2 as Grid,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { MainLayoutContainer } from "components/Layout";
import { DomainColorSquares } from "components/Requirements";
import { TagManager } from "components/TagManager";
import { Tag } from "types/tag";
import { EntitySelectorWithTabs } from "modals/EntitySelector";
import { getFilteredList } from "utils/Array";
import { AdminRoutes } from "config/routes";
import {
  useAddAcademicYearMutation,
  useGetAcademicYearsByIdLazyQuery,
  useUpdateAcademicYearMutation,
  useAddAcademicYearTagMutation,
  useDeleteAcademicYearTagMutation,
  useAddAcademicYearRequirementMutation,
  useDeleteAcademicYearRequirementMutation,
  useGetOrgRequirementsByOrgLazyQuery,
  useGetRequirementsLazyQuery,
} from "graphql/__generated__/hooks";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { AcademicYear } from "types/academicYear";
import {
  getNewEntitiesIds,
  getRemovedEntitiesRelationIds,
} from "utils/FilterEntities";
import { Requirement } from "types/requirement";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { Messages, NotificationSeverity, REQUIREMENT } from "helpers/constants";
import { useNotifications } from "hooks/useNotifications";

type AcademicCreateOrEditProps = { edit?: boolean; program?: boolean };

export const AcademicCreateOrEdit: FC<AcademicCreateOrEditProps> = ({
  edit = false,
  program = false,
}) => {
  const { id, organizationId } = useParams();
  const navigate = useNavigate();
  const [tags, setTags] = useState<Tag[]>([]);
  const [originalTags, setOriginalTags] = useState<Tag[]>([]);
  const [originalRequirements, setOriginalRequirements] = useState<
    Requirement[]
  >([]);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [openEntitySelector, setOpenEntitySelector] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataTags, setDataTags] = useState<
    { academic_year_tag_id: string; tag: Tag }[]
  >([]);

  const [addAcademicYear] = useAddAcademicYearMutation();
  const [updateAcademicYear] = useUpdateAcademicYearMutation();
  const [addAcademicYearTag] = useAddAcademicYearTagMutation();
  const [deleteAcademicYearTag] = useDeleteAcademicYearTagMutation();
  const [getAcademicYearById, { loading: loadingData, data }] =
    useGetAcademicYearsByIdLazyQuery({ variables: { academic_year_id: id } });
  const [getRequirements, { data: reqData }] = useGetRequirementsLazyQuery();
  const [getOrgRequirementsByOrg, { data: orgData }] =
    useGetOrgRequirementsByOrgLazyQuery();

  const reqList = useMemo<Requirement[]>(() => {
    if (reqData) {
      return reqData.requirement as Requirement[];
    }
    if (orgData) {
      return orgData.organization_requirement.map(
        (req) => req.requirement as Requirement,
      );
    }
    return [];
  }, [reqData, orgData]);

  useEffect(() => {
    if (organizationId) {
      getOrgRequirementsByOrg({
        variables: {
          orgId: organizationId,
        },
      });
    } else {
      getRequirements();
    }
  }, [organizationId]);

  const [addAcademicYearRequirement] = useAddAcademicYearRequirementMutation();
  const [deleteAcademicYearRequirement] =
    useDeleteAcademicYearRequirementMutation();

  const { notify } = useNotifications();

  const backRoute = useMemo(
    () =>
      program
        ? `/organization/${organizationId}/yearly-curriculum/`
        : AdminRoutes.AcademicYearList,
    [organizationId, program],
  );

  const initialValues = {
    name: "",
    start_date: "",
    end_date: "",
  };

  useEffect(() => {
    if (edit) {
      getAcademicYearById();
    }
  }, [edit]);

  useEffect(() => {
    if (data) {
      formik.setValues({
        name: data.academic_year_by_pk?.name ?? "",
        start_date: data.academic_year_by_pk?.start_date ?? "",
        end_date: data.academic_year_by_pk?.end_date ?? "",
      });
      setDataTags(data.academic_year_by_pk?.tags || []);
      const currentTags = data.academic_year_by_pk?.tags.map(
        (tag) => tag.tag,
      ) as Tag[];
      const currentRequirements =
        (data.academic_year_by_pk?.requirements.map(
          (req) => req.requirement,
        ) as Requirement[]) || [];
      setTags(currentTags);
      setOriginalTags(currentTags);
      setRequirements(currentRequirements);
      setOriginalRequirements(currentRequirements);
    }
  }, [data]);

  const validationSchema = () =>
    Yup.object().shape({
      name: Yup.string().required("This field is required"),
    });

  const handleAcademicYearAndTags = async (academicYearId: string) => {
    if (edit) {
      const newTagsIds = getNewEntitiesIds(
        originalTags,
        tags,
        "tag_id",
      ) as string[];

      const removedAcademicYearTagsIds = getRemovedEntitiesRelationIds(
        originalTags,
        tags,
        dataTags,
        "tag_id",
        "academic_year_tag_id",
        "tag",
      ) as string[];

      for (const tagId of newTagsIds) {
        await addAcademicYearTag({
          variables: {
            academicYearId: academicYearId,
            tagId: tagId,
          },
        });
      }
      if (!removedAcademicYearTagsIds) return;
      for (const academicYearTagid of removedAcademicYearTagsIds) {
        await deleteAcademicYearTag({
          variables: {
            id: academicYearTagid,
          },
        });
      }
    } else {
      if (tags.length === 0) return;

      tags.forEach(async (tag) => {
        await addAcademicYearTag({
          variables: {
            academicYearId: academicYearId,
            tagId: tag.tag_id,
          },
        });
      });
    }
  };

  const handleRequirements = async (academicYearId: string) => {
    // Edit mode
    if (edit) {
      const newRequirementIds = getNewEntitiesIds(
        originalRequirements,
        requirements,
        "requirement_id",
      ) as string[];

      const removedAcademicYearRequirementIds = getRemovedEntitiesRelationIds(
        originalRequirements,
        requirements,
        data?.academic_year_by_pk?.requirements || [],
        "requirement_id",
        "academic_year_requirement_id",
        "requirement",
      ) as string[];

      const newData = newRequirementIds.map((newRequirementId) => ({
        academic_year_id: academicYearId,
        requirement_id: newRequirementId,
      }));
      await addAcademicYearRequirement({ variables: { objects: newData } });

      if (!removedAcademicYearRequirementIds) return;
      for (const removedAcademicYearRequirementId of removedAcademicYearRequirementIds) {
        await deleteAcademicYearRequirement({
          variables: {
            id: removedAcademicYearRequirementId,
          },
        });
      }
    } else {
      const data = requirements.map((req) => ({
        academic_year_id: academicYearId,
        requirement_id: req.requirement_id,
      }));
      await addAcademicYearRequirement({ variables: { objects: data } });
    }
  };

  /* Create program */
  const handleCreateAcademicYear = async (reqValues: AcademicYear) => {
    setLoading(true);

    const result = await addAcademicYear({
      variables: reqValues,
    });

    const academicYearId =
      result.data?.insert_academic_year?.returning?.[0]?.academic_year_id;
    if (academicYearId) {
      await handleAcademicYearAndTags(academicYearId).then();
      await handleRequirements(academicYearId).then();
    }
    setLoading(false);
    navigate(backRoute);
    notify({
      message: Messages.successAcademicYearCreated,
      severity: NotificationSeverity.success,
    });
  };

  /* Edit program */
  const handleEditAcademicYear = async (reqValues: AcademicYear) => {
    setLoading(true);

    await updateAcademicYear({
      variables: {
        id: id ?? "",
        academicYear: reqValues,
      },
    });
    if (id) {
      await handleAcademicYearAndTags(id).then();
      await handleRequirements(id).then();
    }
    setLoading(false);
    navigate(backRoute);
    notify({
      message: Messages.successAcademicYearUpdated,
      severity: NotificationSeverity.success,
    });
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const reqValues = {
        name: values.name,
        start_date: values.start_date,
        end_date: values.end_date,
      };
      if (edit) {
        handleEditAcademicYear(reqValues as AcademicYear).then();
      } else {
        handleCreateAcademicYear(reqValues as AcademicYear).then();
      }
    },
  });

  const handleAddTag = async (tag: Tag) => {
    setTags([...tags, tag]);
  };

  const handleDeleteTag = async (tagToRemove: Tag) => {
    const newTags = tags.filter((tag) => tag.tag_id !== tagToRemove.tag_id);
    setTags(newTags);
  };

  return (
    <MainLayoutContainer
      title={`${edit ? "Edit" : "Create"} Program`}
      loading={edit && loadingData}
      breadcrumb={
        organizationId
          ? BREADCRUMB_PAGE.YEARLY_CURRICULUM
          : BREADCRUMB_PAGE.ACADEMIC_YEAR
      }
    >
      <Box sx={{ maxWidth: 860 }}>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <Box mb={2}>
              <TextField
                variant="standard"
                onChange={formik.handleChange}
                fullWidth
                name="name"
                label="Name"
                type="text"
                id="name"
                value={formik?.values?.name}
              />
              <ErrorMessage name="name" />
            </Box>

            {program && (
              <Grid container spacing={2}>
                <Grid size={{ md: 6, xs: 12 }}>
                  <Box mb={2}>
                    <TextField
                      variant="standard"
                      onChange={formik.handleChange}
                      fullWidth
                      name="start_date"
                      label="Start Date"
                      type="date"
                      id="start_date"
                      value={formik?.values?.start_date}
                    />
                    <ErrorMessage name="start_date" />
                  </Box>
                </Grid>
                <Grid size={{ md: 6, xs: 12 }}>
                  <Box mb={2}>
                    <TextField
                      variant="standard"
                      onChange={formik.handleChange}
                      fullWidth
                      name="end_date"
                      label="End Date"
                      type="date"
                      id="end_date"
                      value={formik?.values?.end_date}
                    />
                    <ErrorMessage name="end_date" />
                  </Box>
                </Grid>
              </Grid>
            )}

            <TagManager
              tags={tags}
              onAdd={handleAddTag}
              onDelete={handleDeleteTag}
              isEditing={true}
            />

            <Box mt={2}>
              <Typography variant="h6">Requirements</Typography>

              <Stack spacing={2}>
                {requirements.length === 0 && (
                  <Typography variant="body2" align="center">
                    No requirement, add more requirement.
                  </Typography>
                )}
                {requirements.length > 0 && (
                  <List>
                    {requirements.map((requirement, i) => (
                      <ListItem
                        key={requirement.requirement_id}
                        sx={{
                          borderBottom:
                            i === requirements.length - 1
                              ? "none"
                              : "1px solid rgba(0,0,0,0.2)",
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => {
                              setRequirements(
                                requirements.filter(
                                  (data) =>
                                    data.requirement_id !==
                                    requirement.requirement_id,
                                ),
                              );
                            }}
                          >
                            <Delete />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={requirement.name}
                          secondary={
                            <Stack spacing={0.5} alignItems="flex-start">
                              {requirement.description?.slice(0, 120) ? (
                                <span>
                                  {requirement.description.slice(0, 120)}
                                </span>
                              ) : null}
                              <DomainColorSquares requirement={requirement} />
                            </Stack>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}

                <Box></Box>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setOpenEntitySelector(true);
                  }}
                  startIcon={<Add />}
                >
                  Add Requirement
                </Button>
              </Stack>
            </Box>

            <EntitySelectorWithTabs
              open={openEntitySelector}
              onClose={() => setOpenEntitySelector(false)}
              title={"Select Requirements"}
              listEntities={getFilteredList(
                reqList,
                requirements,
                "requirement_id",
              )}
              filterCriteria={(search: string, data: any) =>
                data.name.toLowerCase().includes(search.toLowerCase())
              }
              onSelect={(selected: any) => {
                const toAdd = Array.isArray(selected) ? selected : [selected];
                setRequirements([...requirements, ...toAdd]);
              }}
              displayField="name"
              keyField="requirement_id"
              multiSelect
              tabs={[
                {
                  label: "EPA",
                  filterTabCriteria: (requirement: any) =>
                    requirement?.type === REQUIREMENT.REQUIREMENT_EPA,
                },
                {
                  label: "Competency",
                  filterTabCriteria: (requirement: any) =>
                    requirement?.type === REQUIREMENT.REQUIREMENT_COMPETENCY,
                },
                {
                  label: "ITER",
                  filterTabCriteria: (requirement: any) =>
                    requirement?.type === REQUIREMENT.REQUIREMENT_ITER,
                },
              ]}
            />

            <Box mt={2}>
              <Stack direction="row-reverse" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading}
                >
                  Save Changes
                </Button>
                <Button variant="outlined" onClick={() => navigate(backRoute)}>
                  Cancel
                </Button>
              </Stack>
            </Box>
          </form>
        </FormikProvider>
      </Box>
    </MainLayoutContainer>
  );
};
